import { useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import '../styles/AdminStyles/WebinarAdmin.css';
import AdminShell, { AdminSection } from "../components/WebinarAdmin/AdminShell";
import StorageErrorNotice from "../components/EventsSection/StorageErrorNotice";
import AdminSignIn from "../components/WebinarAdmin/AdminSignIn";
import AdminActivityPanel from "../components/WebinarAdmin/AdminActivityPanel";
import AdminOverview from "../components/WebinarAdmin/AdminOverview";
import WebinarTable from "../components/WebinarAdmin/WebinarTable";
import JoiningInstructionsPanel from "../components/WebinarAdmin/JoiningInstructionsPanel";
import RegistrationsPanel from "../components/WebinarAdmin/RegistrationsPanel";
import RegistrationDetail from "../components/WebinarAdmin/RegistrationDetail";
import WebinarEditor from "../components/WebinarAdmin/WebinarEditor";
import EmailQuotaPanel from "../components/WebinarAdmin/EmailQuotaPanel";
import ProjectUsagePanel from "../components/WebinarAdmin/ProjectUsagePanel";
import ManualMessageDialog from "../components/WebinarAdmin/ManualMessageDialog";
import { isAdminError, RegistrationFilters } from "../webinars/types";
import { prototypeTools, webinarRepository } from "../webinars/repository";
import { useRepositoryData } from "../webinars/useRepositoryData";
import { indexDeliveries } from "../webinars/emailStatus";

// Webinar administration: add and edit webinars, review registrations, send joining instructions.
// In production every query here is checked by the database's Row Level Security; the sign-in
// screen is a convenience, not the protection.
// Two addresses: /admin/login (the Client Admin button) signs in, then moves on to /admin;
// /admin sends anyone signed out to /admin/login, including after Sign out.

const WebinarAdmin = ({ page = "dashboard" }: { page?: "dashboard" | "login" }) => {
  useEffect(() => {
    const previous = document.title;
    document.title = page === "login" ? "Client Admin sign-in | Kole Medical" : "Webinar admin | Kole Medical";
    return () => { document.title = previous; };
  }, [page]);

  const { data: session, loading, error } = useRepositoryData(() => webinarRepository.getAdminSession(), "admin-session");

  if (error) {
    return <AdminShell><StorageErrorNotice error={error} what="the admin dashboard" /></AdminShell>;
  }
  if (loading && session === undefined) {
    return <AdminShell><p className="wa-empty" role="status">Loading…</p></AdminShell>;
  }
  if (!session) {
    if (page !== "login") return <Navigate to="/admin/login" replace />;
    return <AdminShell><AdminSignIn /></AdminShell>;
  }
  if (!session.isAdmin) {
    return (
      <AdminShell session={session}>
        <div className="wa-panel wa-signin">
          <h1>No administrator access</h1>
          <p className="wa-muted">
            {session.email} is signed in but isn't listed as a webinar administrator. Ask the site owner to add this
            account, or sign in with a different one.
          </p>
          <button type="button" className="km-btn km-btn--primary" onClick={() => webinarRepository.signOut()}>Sign out</button>
        </div>
      </AdminShell>
    );
  }
  // Signed in (the prototype always is): the sign-in page has nothing more to do
  if (page === "login") return <Navigate to="/admin" replace />;
  return <AdminShell session={session} sections={DASHBOARD_SECTIONS}><Dashboard /></AdminShell>;
};

/** The dashboard's parts, for the section bar (desktop) and the menu drawer (phones) */
const DASHBOARD_SECTIONS: AdminSection[] = [
  { id: "overview", label: "Overview" },
  { id: "webinars", label: "Webinars" },
  { id: "attendees", label: "Attendees" },
  // The prototype keeps no audit log
  ...(webinarRepository.backend === "supabase" ? [{ id: "activity", label: "Activity log" }] : []),
];

function Dashboard() {
  // Bumped when the daily email limit resets, to reload the figures
  const [quotaEpoch, setQuotaEpoch] = useState(0);
  const { data, loading, error } = useRepositoryData(async () => {
    const [webinars, registrations, counts, deliveries, meetingLinks, quota] = await Promise.all([
      webinarRepository.listAllWebinars(),
      // Archived ones too, so they can be opened from the attendee list; counts use the current round
      webinarRepository.listRegistrations({ round: "all" }),
      webinarRepository.countActiveRegistrations(),
      webinarRepository.listEmailDeliveries(),
      webinarRepository.getMeetingLinks(),
      webinarRepository.getEmailQuota(),
    ]);
    return { webinars, registrations, counts, deliveries, meetingLinks, quota };
  }, `admin|${quotaEpoch}`);

  const resetsAt = data?.quota.resetsAt;
  useEffect(() => {
    if (!resetsAt) return;
    // A couple of seconds after 00:00 UTC (02:00 SAST); capped to what setTimeout accepts
    const wait = Math.min(Math.max(Date.parse(resetsAt) - Date.now() + 2000, 1000), 2 ** 31 - 1);
    const timer = window.setTimeout(() => setQuotaEpoch(epoch => epoch + 1), wait);
    return () => window.clearTimeout(timer);
  }, [resetsAt]);

  const [filters, setFilters] = useState<RegistrationFilters>({ sort: "newest" });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [joiningFor, setJoiningFor] = useState<string | null>(null);
  /** The webinar being edited; "new" while adding one */
  const [editing, setEditing] = useState<string | null>(null);
  /** The webinar whose selected attendees are getting a manual message */
  const [manualFor, setManualFor] = useState<string | null>(null);
  const [selection, setSelection] = useState<string[]>([]);
  const [toast, setToast] = useState("");
  const toastTimer = useRef(0);

  useEffect(() => () => window.clearTimeout(toastTimer.current), []);

  const notify = (message: string) => {
    setToast(message);
    window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(""), 2800);
  };

  const scrollTo = (id: string) =>
    window.requestAnimationFrame(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" }));

  const showAttendees = (webinarId: string) => {
    if (webinarId !== filters.webinarId) setSelection([]);
    setFilters(prev => ({ ...prev, webinarId, search: "", status: undefined, emailIssues: undefined, round: undefined }));
    scrollTo("attendees");
  };

  const openJoining = (webinarId: string) => {
    setJoiningFor(webinarId);
    scrollTo("joining");
  };

  const addSamples = async () => {
    const added = await prototypeTools!.addSampleRegistrations();
    notify(added > 0 ? `Added ${added} sample registrations` : "Sample registrations are already in place");
  };

  const resetData = async () => {
    if (!window.confirm("Reset the prototype? This deletes every registration and joining link saved in this browser and restores the demonstration webinars.")) return;
    await prototypeTools!.resetToDemoData();
    setSelectedId(null);
    setJoiningFor(null);
    setSelection([]);
    setFilters({ sort: "newest" });
    notify("Prototype data reset");
  };

  const webinars = data?.webinars ?? [];
  const webinarsById = Object.fromEntries(webinars.map(w => [w.id, w]));
  const deliveries = indexDeliveries(data?.deliveries ?? []);
  const selected = data?.registrations.find(r => r.id === selectedId);
  const current = (data?.registrations ?? []).filter(r => !r.archivedAt);
  const joiningWebinar = joiningFor ? webinarsById[joiningFor] : undefined;

  return (
    <>
      <div className="wa-titlebar">
        <div>
          <h1>Webinars &amp; attendees</h1>
          <p>Add and edit webinars, review registrations, send joining instructions and export attendee lists.</p>
        </div>
        {prototypeTools && (
          <div className="wa-titlebar-actions">
            <button type="button" className="wa-btn wa-btn--quiet" onClick={addSamples}>Add sample registrations</button>
            <button type="button" className="wa-btn wa-btn--quiet" onClick={resetData}>Reset prototype data</button>
          </div>
        )}
      </div>

      {error ? (
        isAdminError(error)
          ? <p className="wa-empty" role="status">{error.message}</p>
          : <StorageErrorNotice error={error} what="the admin data" />
      ) : loading && !data ? (
        <p className="wa-empty" role="status">Loading…</p>
      ) : data && (
        <>
          <div id="overview" className="wa-section">
            <AdminOverview webinars={webinars} registrations={current} deliveries={data.deliveries} />

            {/* Monitoring: compact, and secondary to the webinars and attendees below */}
            <div className="wa-monitor-grid">
              <EmailQuotaPanel quota={data.quota} notify={notify} />
              {/* The prototype has no Supabase project to measure */}
              {webinarRepository.backend === "supabase" && <ProjectUsagePanel notify={notify} />}
            </div>
          </div>

          <WebinarTable
            webinars={webinars}
            counts={data.counts}
            registrations={current}
            deliveries={deliveries}
            meetingLinks={data.meetingLinks}
            onShowAttendees={showAttendees}
            onJoiningInstructions={openJoining}
            onAdd={() => setEditing("new")}
            onEdit={setEditing}
          />

          {joiningWebinar && (
            <JoiningInstructionsPanel
              key={joiningWebinar.id}
              webinar={joiningWebinar}
              savedLink={data.meetingLinks[joiningWebinar.id] ?? ""}
              quota={data.quota}
              registrations={current.filter(r => r.webinarId === joiningWebinar.id)}
              deliveries={deliveries}
              selectedIds={filters.webinarId === joiningWebinar.id ? selection : []}
              onClearSelection={() => setSelection([])}
              onClose={() => setJoiningFor(null)}
              notify={notify}
            />
          )}

          <RegistrationsPanel
            webinars={webinars}
            hasAnyRegistrations={data.registrations.length > 0}
            deliveries={deliveries}
            filters={filters}
            onFiltersChange={setFilters}
            onOpen={setSelectedId}
            selectedIds={selection}
            onSelectionChange={setSelection}
            onJoiningInstructions={openJoining}
            onPrepareManual={setManualFor}
            onAddSamples={addSamples}
            notify={notify}
          />

          {/* Production only: the prototype keeps no audit log */}
          {webinarRepository.backend === "supabase" && <AdminActivityPanel webinarsById={webinarsById} />}
        </>
      )}

      {selected && (
        <RegistrationDetail
          key={selected.id}
          registration={selected}
          webinar={webinarsById[selected.webinarId]}
          meetingLink={data?.meetingLinks[selected.webinarId] ?? ""}
          deliveries={deliveries[selected.id] ?? {}}
          onClose={() => setSelectedId(null)}
          notify={notify}
        />
      )}

      {manualFor && webinarsById[manualFor] && (
        <ManualMessageDialog
          webinar={webinarsById[manualFor]}
          registrations={(data?.registrations ?? []).filter(r => selection.includes(r.id) && r.webinarId === manualFor)}
          meetingLink={data?.meetingLinks[manualFor] ?? ""}
          onClose={() => setManualFor(null)}
          notify={notify}
        />
      )}

      {editing && (editing === "new" || webinarsById[editing]) && (
        <WebinarEditor
          key={editing}
          webinar={editing === "new" ? undefined : webinarsById[editing]}
          registeredCount={editing === "new" ? 0 : data?.counts[editing] ?? 0}
          onClose={() => setEditing(null)}
          notify={notify}
        />
      )}

      {toast && <div className="wa-toast" role="status">{toast}</div>}
    </>
  );
}

export default WebinarAdmin;
